import { sdqQuestions, sdqRatingScale, calculateSDQScores, getSDQSeverity } from '../../sdqQuestions';
import { getAssessmentExamples } from '../../assessmentExamples';
import { ensureSession, fetchProgress, resumePoint, saveProgress, completeSession } from '../sessionApi';
import { guidanceOfficeFact, inTurn, NOTED, HEAVY, HONEST } from './common';

const TOTAL = sdqQuestions.length;

const LABELS = [
  { en: 'Not true', fil: 'Hindi totoo' },
  { en: 'Somewhat true', fil: 'Medyo totoo' },
  { en: 'Certainly true', fil: 'Talagang totoo' },
];

// The backend's score field names
const toBackendScores = (raw) => ({
  emotional_symptoms_score: raw.emotional_symptoms,
  conduct_problems_score: raw.conduct_problems,
  hyperactivity_score: raw.hyperactivity,
  peer_problems_score: raw.peer_problems,
  prosocial_score: raw.prosocial,
  total_difficulties_score: raw.total_difficulties,
  internalising_score: raw.internalising,
  externalising_score: raw.externalising,
});

/** A "Very High" total difficulties score (20 or more on the self-report). */
export const sdqIsWorrying = (answers) => getSDQSeverity(calculateSDQScores(answers)).total_difficulties === 'Very High';

const sdq = {
  key: 'sdq',
  total: TOTAL,
  feedbackCategory: 'sdq',
  followupReason: 'sdq_followup',
  resultsTab: 'sdq',
  names: {
    title: { en: 'Strengths and difficulties', fil: 'Strengths and difficulties' },
    short: { en: 'Strengths and difficulties', fil: 'Strengths and difficulties' },
    code: 'SDQ',
  },
  greeting: {
    en: "👋 Hello! Let's start the SDQ Assessment. This is a questionnaire that will help us understand your strengths and difficulties.",
    fil: '👋 Hello! Magsisimula na tayo sa SDQ Assessment. Ito ay isang questionnaire na makakatulong sa amin na maunawaan ang inyong strengths at difficulties.',
  },
  facts: (lang, school) => [
    { icon: 'list', text: lang === 'fil' ? `${TOTAL} maiikling pahayag` : `${TOTAL} short statements` },
    { icon: 'calendar', text: lang === 'fil' ? 'Isipin ang nakaraang buwan' : 'Think about the past month' },
    guidanceOfficeFact(lang, school),
  ],
  layout: 'row',
  scale: sdqRatingScale.map((option, i) => ({ value: option.value, label: LABELS[i], sub: null })),
  prompt: { en: 'In the past month, is this true for you?', fil: 'Sa nakaraang buwan, totoo ba ito sa iyo?' },
  questionText: (i, lang) => {
    const q = sdqQuestions[i];
    return (lang === 'fil' ? q.questionFil : q.question) || q.question;
  },
  examples: (i, lang) => {
    const q = sdqQuestions[i];
    return (lang === 'fil' ? q.examplesFil : q.examples) || getAssessmentExamples('sdq', q.id, lang) || [];
  },
  echo: (value, lang) => `${value} · ${LABELS[value]?.[lang === 'fil' ? 'fil' : 'en'] || value}`,
  checkpoints: {
    13: { en: 'Halfway! Just 12 more.', fil: 'Kalahati na! 12 na lang.' },
    20: { en: 'Only 5 left. Almost done.', fil: '5 na lang. Malapit nang matapos.' },
  },
  isWorrying: sdqIsWorrying,
  reply: (i, value, lang, n) => {
    const q = sdqQuestions[i];
    const positive = q.type === 'pro' || q.reverse_scored;
    if (!positive && Number(value) === 2) return { care: true, text: inTurn(HEAVY, lang, n) };
    if (positive && Number(value) === 0) return { care: true, text: inTurn(HONEST, lang, n) };
    return { text: inTurn(NOTED, lang, n) };
  },

  load: async () => {
    const point = resumePoint(await fetchProgress('sdq'), TOTAL);
    const answers = Array.isArray(point.responses?.answers) ? point.responses.answers.slice(0, TOTAL) : [];
    return { sessionId: point.sessionId, nextIndex: point.nextIndex, answers };
  },
  save: async ({ sessionId, nextIndex, answers }) => {
    const id = await ensureSession('sdq', TOTAL, sessionId);
    await saveProgress({
      session_id: id,
      assessment_type: 'sdq',
      current_step: nextIndex,
      responses: { answers, scores: toBackendScores(calculateSDQScores(answers)) },
    });
    return id;
  },
  complete: async ({ sessionId, answers }) => {
    const id = await ensureSession('sdq', TOTAL, sessionId);
    const raw = calculateSDQScores(answers);
    const severity = getSDQSeverity(raw);
    const scores = {
      ...toBackendScores(raw),
      impact_score:
        raw.total_difficulties > 19
          ? Math.min(10, Math.floor(raw.total_difficulties / 4))
          : raw.total_difficulties > 15
            ? Math.min(5, Math.floor(raw.total_difficulties / 6))
            : 0,
      emotional_severity: severity.emotional_symptoms,
      conduct_severity: severity.conduct_problems,
      hyperactivity_severity: severity.hyperactivity,
      peer_severity: severity.peer_problems,
      prosocial_severity: severity.prosocial,
      total_difficulties_severity: severity.total_difficulties,
    };
    await completeSession(id, {
      assessment_type: 'sdq',
      final_responses: { answers, scores },
      scores,
      interpretation: Object.entries(severity)
        .map(([dimension, level]) => `${dimension.replace('_', ' ')}: ${level}`)
        .join(', '),
    });
    return id;
  },
};

export default sdq;
